import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import { ChevronRight, Sun, Moon } from "lucide-react";

export default function Auth() {
  const { signup, signin, user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [league, setLeague] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/matches", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        if (!name.trim() || !league.trim()) {
          toast.error("Fill all fields");
          setLoading(false);
          return;
        }
        await signup(name.trim(), league.trim());
        try { sessionStorage.setItem("mm_show_onboarding", "1"); } catch (e) { /* ignore */ }
        toast.success(`Welcome ${name.split(" ")[0]}, you're in the league.`);
      } else {
        if (!league.trim()) {
          toast.error("Enter your league name");
          setLoading(false);
          return;
        }
        const u = await signin(league.trim());
        toast.success(`Back at it, ${u.name.split(" ")[0]}.`);
      }
      navigate("/matches");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
      {/* Floating theme toggle */}
      <button
        onClick={toggleTheme}
        data-testid="auth-theme-toggle"
        title={theme === "dark" ? "Light mode" : "Dark mode"}
        className="absolute top-5 right-5 z-20 w-11 h-11 rounded-full glass grid place-items-center hover:scale-105 transition-transform"
      >
        {theme === "dark" ? <Sun size={17} className="text-lavender"/> : <Moon size={17} className="text-purple"/>}
      </button>
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt=""
          className="w-full h-full object-cover opacity-25 dark-only-img"
        />
        <div className="absolute inset-0 auth-overlay" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(139,32,176,0.25),transparent_40%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(232,180,240,0.12),transparent_40%)]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple/15 border border-purple/40 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-lavender animate-pulse" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-lavender">FIFA 2026 · Live</span>
          </div>
          <div className="relative inline-block">
            <h1 className="font-display text-6xl sm:text-7xl tracking-[0.04em] uppercase leading-none">
              MOMENTUM
            </h1>
            <div className="absolute -inset-x-4 -bottom-2 h-px bg-gradient-to-r from-transparent via-lavender to-transparent" />
          </div>
          <p className="text-white/60 mt-5 max-w-sm mx-auto text-sm">
            The internal company prediction league. Pick scores, BTTS, the champion. Outsmart your colleagues.
          </p>
        </div>

        <div className="flex p-1 rounded-full border border-white/10 bg-surface/70 mb-6">
          {[{ k: "signin", l: "Sign In" }, { k: "signup", l: "Sign Up" }].map((m) => (
            <button
              key={m.k}
              type="button"
              onClick={() => setMode(m.k)}
              data-testid={`tab-${m.k}`}
              className={`flex-1 py-2.5 rounded-full text-xs tracking-[0.2em] uppercase transition-all ${
                mode === m.k ? "bg-gradient-to-r from-vivid to-purple text-white font-bold" : "text-white/50 hover:text-white"
              }`}
            >
              {m.l}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="bg-surface/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8 space-y-5">
          {mode === "signup" && (
            <Field label="Real Name (for prize distribution)">
              <input
                data-testid="signup-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Lionel Messi"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-purple focus:ring-1 focus:ring-purple outline-none transition-all"
              />
            </Field>
          )}

          <Field label={mode === "signup" ? "League Name (your gamer tag)" : "League Name"}>
            <input
              data-testid="auth-league-input"
              value={league}
              onChange={(e) => setLeague(e.target.value)}
              placeholder={mode === "signup" ? "GoalMachine10" : "Your league name"}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-purple focus:ring-1 focus:ring-purple outline-none transition-all"
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="cta-sweep btn-bounce w-full bg-gradient-to-r from-vivid to-purple text-white font-bold uppercase tracking-wide rounded-full px-6 py-3.5 hover:from-purple hover:to-lavender hover:text-plum transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_4px_18px_rgba(160,64,192,0.35)]"
          >
            {loading ? "..." : <>{mode === "signup" ? "Join The League" : "Enter The Arena"} <ChevronRight size={18}/></>}
          </button>

          <p className="text-xs text-white/40 text-center pt-2">
            {mode === "signup"
              ? "Your league name is your login. Real name shows on the leaderboard."
              : "Forgot your league name? Contact the admin."}
          </p>

          <div className="mt-3 p-3 rounded-xl bg-purple/10 border border-purple/30 text-[10px] tracking-wider text-white/60 text-center leading-relaxed" data-testid="auth-support-contact">
            <div className="uppercase tracking-[0.2em] text-lavender font-bold mb-1">Help &amp; Support</div>
            <a href="mailto:abasit@mcorp.ae?cc=Customer_Operations_Team_Igaming@mcorp.ae" className="text-lavender hover:text-purple break-all">abasit@mcorp.ae</a>
            <div className="mt-0.5">cc <span className="text-white/50 break-all">Customer_Operations_Team_Igaming@mcorp.ae</span></div>
          </div>
        </form>

        <div className="mt-8 grid grid-cols-4 gap-3 text-center">
          {[
            { v: "+5", l: "Exact" },
            { v: "+3", l: "Winner" },
            { v: "+1", l: "BTTS" },
            { v: "+52", l: "Bonus" },
          ].map((s, i) => (
            <div key={i} className="bg-surface/50 border border-white/5 rounded-xl py-3">
              <div className="font-mono font-bold text-xl text-lavender">{s.v}</div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-white/40 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 block mb-2">{label}</label>
      {children}
    </div>
  );
}
